import { Session } from "@supabase/supabase-js";
import { supabase } from "@/integrations/supabase/client";

export type AppMode = "client" | "trainer" | "unknown";

interface AppModeResult {
  mode: AppMode;
  clientId?: string;
}

/**
 * Determines if the current user is a client or trainer.
 * This is the single source of truth for app mode detection.
 * 
 * Detection order:
 * 1. Check user_metadata.role (set during OTP sign-in for clients)
 * 2. Check if user has a linked client record with portal_enabled
 */
export async function determineAppMode(session: Session | null): Promise<AppModeResult> {
  if (!session?.user) {
    return { mode: "unknown" };
  }

  const userId = session.user.id;
  const userEmail = session.user.email;
  const metadata = session.user.user_metadata;

  // Check 1: user_metadata.role === 'client'
  if (metadata?.role === "client") {
    return { mode: "client", clientId: metadata.client_id };
  }

  // Check 2: Look for linked client record
  try {
    const { data: clientRecord } = await supabase
      .from("clients")
      .select("id, portal_enabled")
      .eq("auth_user_id", userId)
      .eq("portal_enabled", true)
      .maybeSingle();

    if (clientRecord) {
      return { mode: "client", clientId: clientRecord.id };
    }

    // Check 3: Look for client by email (fallback for unlinked accounts)
    if (userEmail) {
      const { data: clientByEmail } = await supabase
        .from("clients")
        .select("id, portal_enabled, auth_user_id")
        .eq("email", userEmail.toLowerCase())
        .eq("portal_enabled", true)
        .maybeSingle();

      if (clientByEmail) {
        // Link the account if not already linked
        if (!clientByEmail.auth_user_id) {
          await supabase
            .from("clients")
            .update({
              auth_user_id: userId,
              portal_last_login: new Date().toISOString()
            })
            .eq("id", clientByEmail.id);
        }

        return { mode: "client", clientId: clientByEmail.id };
      }
    }
  } catch (error) {
    console.error("[Auth] Error checking client status:", error);
  }

  // If no client record found, user is a trainer
  return { mode: "trainer" };
}

/**
 * Quick check if user should be redirected to portal.
 * Used by guards to prevent trainer UI flash.
 */
export async function isClientUser(session: Session | null): Promise<boolean> {
  const result = await determineAppMode(session);
  return result.mode === "client";
}
